import isEqual from 'lodash/isEqual';
import { FieldInputProps, useField } from 'react-final-form';

import { MultipleAutocompleteInput as Input } from '@vk-hr-tek/ui/input';

import { compose } from '../validation';

import { MultipleAutocompleteInputProps } from './MultipleAutocompleteInput.types';

/**
 * Функция-обработчик изменения значения поля
 *
 * @param input - Пропсы поля из react-final-form
 * @param onChange - Дополнительная функция-обработчик изменения значения
 * @returns Функция, которая вызывает обработчики изменения значения
 */
const handleChange =
  (
    input: FieldInputProps<
      string | string[] | number | number[] | undefined,
      HTMLElement
    >,
    onChange: ((value: string[] | undefined) => void) | undefined,
  ) =>
  (value: string[] | undefined) => {
    input.onChange(value);

    onChange?.(value);
  };

/**
 * Компонент для выбора нескольких элементов из автозаполняющегося списка.
 *
 * @public
 * @param label - Метка для поля ввода.
 * @param labelIcon - Иконка для метки.
 * @param onChange - Дополнительная функция для обработки изменения значения.
 * @param tooltip - Тултип для поля.
 * @param name - Уникальное имя поля для react-final-form.
 * @param placeholder - Текст плейсхолдера.
 * @param required - Флаг обязательности поля.
 * @param loading - Флаг загрузки данных.
 * @param limit - Ограничение количества загружаемых опций.
 * @param disabledTooltip - Тултип при отключенном состоянии.
 * @param disabled - Флаг отключения поля.
 * @param clearable - Флаг возможности очистки значения.
 * @param validate - Массив валидаторов.
 * @param helperText - Дополнительный текст помощи.
 * @param minInputValueLength - Минимальная длина значения инпута для начала загрузки опций.
 * @param renderTags - Функция для рендера тегов.
 * @param renderOption - Функция для рендера опции.
 * @param testId - Идентификатор для тестирования.
 * @param withoutMargin - Атрибут для отмены дефолтного маржина в 16 пикселей у инпута.
 * @param validationWithoutTouch - Показывать ошибку без взаимодействия с полем.
 * @param validationWhenDisabled - Показывать ошибку если поле задизейблено.
 * @param type - Тип компонента ('load' или 'local').
 * @param loadItems - Функция для загрузки опций по фильтру (только для type='load').
 * @param externalItems - Внешний список опций (только для type='local').
 * @param selectAllLabel - Метка для кнопки "Выбрать все" (только для type='local').
 * @param allowSelectAll - Флаг разрешения выбора всех опций (только для type='local').
 * @returns Компонент для выбора нескольких элементов.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link MultipleAutocompleteInputProps}
 */
export const MultipleAutocompleteInput = (
  props: MultipleAutocompleteInputProps,
) => {
  const {
    label,
    labelIcon,
    onChange,
    tooltip,
    name,
    placeholder,
    required,
    loading,
    limit,
    disabledTooltip,
    disabled,
    clearable,
    validate = [],
    helperText,
    minInputValueLength,
    renderTags,
    renderOption,
    testId,
    withoutMargin = false,
    validationWithoutTouch = false,
    validationWhenDisabled = false,
  } = props;
  const { input, meta } = useField(name, {
    validate: compose<string[]>(validate),
    isEqual,
  });

  const errorDisabled = disabled || !validationWhenDisabled;

  const renderError =
    !errorDisabled && validationWithoutTouch
      ? !!input.value && !!meta.error
      : meta.touched && !!meta.error;

  if (props.type === 'load') {
    return (
      <Input
        {...input}
        type={props.type}
        label={label}
        labelIcon={labelIcon}
        onChange={handleChange(input, onChange)}
        loading={loading}
        tooltip={tooltip}
        placeholder={placeholder}
        required={required}
        limit={limit}
        disabledTooltip={disabledTooltip}
        disabled={disabled}
        clearable={clearable}
        error={renderError}
        helperText={(renderError && meta.error) || helperText}
        loadItems={props.loadItems}
        minInputValueLength={minInputValueLength}
        renderTags={renderTags}
        renderOption={renderOption}
        testId={testId}
        withoutMargin={withoutMargin}
      />
    );
  }

  return (
    <Input
      {...input}
      type={props.type}
      label={label}
      labelIcon={labelIcon}
      onChange={handleChange(input, onChange)}
      loading={loading}
      tooltip={tooltip}
      placeholder={placeholder}
      required={required}
      limit={limit}
      disabledTooltip={disabledTooltip}
      disabled={disabled}
      clearable={clearable}
      error={renderError}
      helperText={(renderError && meta.error) || helperText}
      externalItems={props.externalItems}
      selectAllLabel={props.selectAllLabel}
      allowSelectAll={props.allowSelectAll}
      minInputValueLength={minInputValueLength}
      renderTags={renderTags}
      renderOption={renderOption}
      testId={testId}
      withoutMargin={withoutMargin}
    />
  );
};
